/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useEffect, useRef, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { DocNode } from '@atlaskit/adf-schema';

import { AIAgentResponseState, useAIAgentStreaming } from '@atlassian/ai-agent-streaming';
import { markdownPlusToAdf, adfToMarkdownPlus } from '@atlassian/adf-markdown-transformer';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { AiIssueCreateState } from '@atlassian/ai-issue-create';
import { useConfluencePageAri } from '@atlassian/ai-summary';

import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type { Issue, JiraAIRequest, JiraAIResponse } from '../__types__/AiAgentStreamingTypes';
import { AiStates, useBulkCreateContext } from '../providers/BulkCreateContextProvider';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents';
import type {
	useAIAgentStreamingDataBulkIssueADFQuery as useAIAgentIssuesStreamingDataBulkIssueADFQuery$data,
	useAIAgentStreamingDataBulkIssueADFQueryVariables as useAIAgentIssuesStreamingDataBulkIssueADFQuery$variables,
} from '../services/__types__/useAIAgentStreamingDataBulkIssueADFQuery';

export const useAIAgentIssuesStreaming = (isConvoAIMigrationEnabled: boolean) => {
	const [{ bulkIssuesList }, { setBulkIssuesList }] = useBulkCreateContext();
	const [markdownPlusContent, setMarkdownPlusContent] = useState('');
	const [pageIdMap, setPageIdMap] = useState<IDMap | undefined>(undefined);
	const [currentAiState, setCurrentAiState] = useState<AiIssueCreateState>(AiStates.LOADING);
	const { logAiStreamingBackendFailed } = useBulkCreateLoggingEvents();
	const { cloudId } = useSessionData();
	const [contentType] = useContentType();

	const [contentId] = usePageContentId();
	const { logAiNoResultsGenerated } = useBulkCreateLoggingEvents();

	const pageId = contentId ?? '';
	const pageType = contentType ?? '';

	const { pageAri } = useConfluencePageAri(cloudId, pageId, pageType);

	// Used to keep track of the issues that have been added to the list and give a unique id
	const issueIndex = useRef(0);

	// Needed to convert confluence content into MD+ format for the ai request
	useQuery<
		useAIAgentIssuesStreamingDataBulkIssueADFQuery$data,
		useAIAgentIssuesStreamingDataBulkIssueADFQuery$variables
	>(
		graphql`
			query useAIAgentIssuesStreamingDataBulkIssueADFQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					body {
						atlas_doc_format {
							value
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: pageId,
			},
			onError: (_adfError) => {
				setCurrentAiState(AiStates.ERROR);
			},
			fetchPolicy: 'network-only',
			onCompleted: (adfData) => {
				const adf = adfData?.singleContent?.body?.atlas_doc_format?.value;
				if (adf) {
					try {
						const parsedAdf: DocNode = JSON.parse(adf);
						const { markdown: markdownPlus, idMap } = adfToMarkdownPlus(parsedAdf);
						// Handle pages that don't have any content
						if (!markdownPlus) {
							setCurrentAiState(AiStates.ERROR);
							return;
						}
						setMarkdownPlusContent(markdownPlus);
						setPageIdMap(idMap);
					} catch (err) {
						setCurrentAiState(AiStates.ERROR);
					}
				}
			},
			skip: currentAiState !== AiStates.LOADING || isConvoAIMigrationEnabled,
		},
	);

	const request: JiraAIRequest = {
		recipient_agent_named_id: 'ai_issue_create_agent',
		agent_input_context: {
			application: 'Confluence',
			context: {
				confluence_page_markdown_plus: markdownPlusContent,
				highlighted_text: null,
				source_ari: pageAri,
			},
			suggested_issues_config: {
				max_issues: 10,
				suggested_issue_field_types: [
					{
						issue_type: 'Task',
						fields: [
							{
								field_name: 'Summary',
								field_type: 'Short-Text',
							},
							{
								field_name: 'Description',
								field_type: 'Paragraph',
							},
						],
					},
				],
			},
		},
	};

	const [{ content, responseState, error }] = useAIAgentStreaming<JiraAIRequest, JiraAIResponse>(
		request,
		{
			enablePartialJson: true,
			skip: !markdownPlusContent || isConvoAIMigrationEnabled,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product': 'confluence',
			},
		},
	);

	useEffect(() => {
		const getIssueDetails = () => {
			return {
				iconUrl: '',
				name: '',
				id: '',
			};
		};

		// Temp add issues until we can handle partial responses
		const addIssues = (issues: Issue[]) => {
			const newIssues = issues.map((issue) => ({
				id: String(issueIndex.current++),
				summary: issue.field_values.Summary,
				description: markdownPlusToAdf({
					markdown: issue.field_values.Description,
					idMap: pageIdMap,
				}),
				issueType: getIssueDetails(),
			}));

			void setBulkIssuesList(newIssues);
		};

		// Switch statement to handle the different response states
		switch (responseState) {
			case AIAgentResponseState.Initialization:
				// Not needed really just want to track all the states
				void setBulkIssuesList([]);
				break;
			case AIAgentResponseState.AnswerPart:
				// Cleaning up the FG for now - will update at a later time with a semi-loadin state for chunk responses
				break;
			case AIAgentResponseState.FinalResponse:
				const issues = content.suggested_issues;
				try {
					if (issues.length === 0) {
						logAiNoResultsGenerated();
						setCurrentAiState(AiStates.ERROR);
					} else {
						// Setting list when all issues are returned for now, will update to handle partial responses
						addIssues(issues);
						setCurrentAiState(AiStates.IDLE);
					}
				} catch (e) {
					setCurrentAiState(AiStates.ERROR);
					logAiStreamingBackendFailed(e);
				}
				break;
			case AIAgentResponseState.Error:
				setCurrentAiState(AiStates.ERROR);
				logAiStreamingBackendFailed(error);
				break;
			default:
				break;
		}
	}, [
		content,
		pageIdMap,
		responseState,
		issueIndex,
		error,
		logAiStreamingBackendFailed,
		logAiNoResultsGenerated,
		setBulkIssuesList,
	]);

	return {
		aiAgentContent: bulkIssuesList,
		aiAgentCurrentAiState: currentAiState,
		aiAgentResponseState: responseState,
	};
};
